export type ZonePoint = [number, number]; // GeoJSON order: longitude, latitude.

export const validZonePoint = ([lng, lat]: ZonePoint) =>
  Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90;

export function openRing(points: ZonePoint[]): ZonePoint[] {
  const ring = points.map(point => [...point] as ZonePoint);
  if (ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]) ring.pop();
  return ring;
}

export const closeRing = (points: ZonePoint[]): ZonePoint[] => points.length ? [...points, [...points[0]] as ZonePoint] : [];

export function polygonError(points: ZonePoint[]): string | null {
  if (points.length < 3) return 'Mark at least three boundary points on the map.';
  if (!points.every(validZonePoint)) return 'One or more boundary points have invalid coordinates.';
  if (new Set(points.map(point => point.join(','))).size !== points.length) return 'Boundary points must be distinct.';
  const cross = (a: ZonePoint, b: ZonePoint, c: ZonePoint) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const onSegment = (a: ZonePoint, b: ZonePoint, c: ZonePoint) => Math.abs(cross(a, b, c)) < 1e-12 && c[0] >= Math.min(a[0], b[0]) && c[0] <= Math.max(a[0], b[0]) && c[1] >= Math.min(a[1], b[1]) && c[1] <= Math.max(a[1], b[1]);
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (j === i + 1 || (i === 0 && j === points.length - 1)) continue;
      const a = points[i], b = points[(i + 1) % points.length], c = points[j], d = points[(j + 1) % points.length];
      if ((cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0) || onSegment(a, b, c) || onSegment(a, b, d) || onSegment(c, d, a) || onSegment(c, d, b)) return 'The boundary crosses itself. Move or remove a point.';
    }
  }
  const area = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + point[0] * next[1] - next[0] * point[1];
  }, 0);
  if (Math.abs(area) < 1e-12) return 'The boundary must enclose an area. Move the points farther apart.';
  return null;
}

export function circleRing(center: ZonePoint, radius: number): ZonePoint[] {
  const radians = Math.PI / 180;
  const lat = center[1] * radians, lng = center[0] * radians;
  const distance = radius / 6371008.8;
  const points: ZonePoint[] = [];
  for (let i = 0; i < 64; i++) {
    const bearing = 2 * Math.PI * i / 64;
    const targetLat = Math.asin(Math.sin(lat) * Math.cos(distance) + Math.cos(lat) * Math.sin(distance) * Math.cos(bearing));
    const targetLng = lng + Math.atan2(Math.sin(bearing) * Math.sin(distance) * Math.cos(lat), Math.cos(distance) - Math.sin(lat) * Math.sin(targetLat));
    points.push([((targetLng / radians + 540) % 360) - 180, targetLat / radians]);
  }
  return closeRing(points);
}
